import type {
  AccountState,
  Entry,
  Exit,
  FlowEvent,
  FlowTable,
  RegistrationContext,
  Step
} from './types';

const EXITS: readonly string[] = [
  'home',
  'homeAddPayment',
  'checkout',
  'onglaye'
];

export function isExit(x: Step | Exit): x is Exit {
  return EXITS.includes(x);
}

const methodFlow = (out: Exit): FlowTable[Entry] => ({
  createAccount: { accountCreated: 'selectMethod' },
  success: {},
  selectMethod: {
    methodAdded: 'methodAdded',
    methodFailed: 'methodError',
    exit: out
  },
  methodError: {
    retry: 'selectMethod',
    methodAdded: 'methodAdded',
    methodFailed: 'methodError',
    exit: out
  },
  methodAdded: { countdownEnded: out }
});

export const REGISTRATION_FLOWS: FlowTable = {
  direct: {
    createAccount: { accountCreated: 'success' },
    success: { addNow: 'homeAddPayment', later: 'home' },
    selectMethod: {},
    methodError: {},
    methodAdded: {}
  },
  checkoutRequired: methodFlow('checkout'),
  onglaye: methodFlow('onglaye'),
  checkoutOptional: {
    createAccount: { accountCreated: 'success' },
    success: {
      addNow: 'selectMethod',
      later: 'checkout',
      countdownEnded: 'checkout'
    },
    selectMethod: {
      methodAdded: 'methodAdded',
      methodFailed: 'methodError',
      exit: 'checkout'
    },
    methodError: {
      retry: 'selectMethod',
      methodAdded: 'methodAdded',
      methodFailed: 'methodError',
      exit: 'checkout'
    },
    methodAdded: { countdownEnded: 'checkout' }
  }
};

const currentStep = (ctx: RegistrationContext): Step =>
  ctx.account?.step ?? 'createAccount';

/**
 * Applies one event. An event with no table entry returns the same context
 * and no exit; an exit leaves the context unchanged for the caller to clear.
 */
export function transition(
  ctx: RegistrationContext,
  event: FlowEvent,
  patch?: Partial<AccountState>
): { ctx: RegistrationContext; exit: Exit | null } {
  const target = REGISTRATION_FLOWS[ctx.entry][currentStep(ctx)][event];
  if (target === undefined) return { ctx, exit: null };
  if (isExit(target)) return { ctx, exit: target };

  const base: AccountState = ctx.account ?? {
    accountCreated: true,
    step: target,
    accessToken: null,
    methodLinked: false,
    failure: null
  };
  const account: AccountState = {
    ...base,
    ...patch,
    accountCreated: true,
    step: target
  };
  if (event === 'methodAdded') {
    account.methodLinked = true;
    account.failure = null;
  }
  if (event === 'retry') account.failure = null;
  if (event === 'methodFailed') account.failure = patch?.failure ?? null;
  return { ctx: { ...ctx, account }, exit: null };
}
